import type {
  MetricSeverity,
  ServerGroup,
  ServerStatsRow,
} from './types';

const ONLINE = 'online';
function isOnlineStatus(status: unknown): boolean {
  return typeof status === 'string' && status.toLowerCase().trim() === ONLINE;
}

/** Severity ladder: any single metric >=80% → critical, >=50% → warning,
 *  else online. An unreachable server is offline regardless of metrics. */
export function getMetricSeverity(server: ServerStatsRow): MetricSeverity {
  if (!isOnlineStatus(server.status)) return 'offline';
  const memTotal = Number(server.memTotal) || 0;
  const memUsed = Number(server.memUsed) || 0;
  const diskTotal = Number(server.diskTotal) || 0;
  const diskUsed = Number(server.diskUsed) || 0;
  const cpuPct = Number(server.cpuUsed) || 0;
  const cores = (Number(server.cpuCores) || 0) > 0 ? Number(server.cpuCores) : 1;
  const loadPct = ((Number(server.load1m) || 0) / cores) * 100;
  const memPct = memTotal > 0 ? (memUsed / memTotal) * 100 : 0;
  const diskPct = diskTotal > 0 ? (diskUsed / diskTotal) * 100 : 0;
  if (cpuPct >= 80 || memPct >= 80 || diskPct >= 80 || loadPct >= 80)
    return 'critical';
  if (cpuPct >= 50 || memPct >= 50 || diskPct >= 50 || loadPct >= 50)
    return 'warning';
  return 'online';
}

/** Group servers by region suffix in the name — `_AHM` / `_BLR`. */
export function getGroup(serverName: string | undefined): ServerGroup {
  if (!serverName) return 'db';
  const n = serverName.toUpperCase();
  if (n.includes('_AHM')) return 'ahm';
  if (n.includes('_BLR')) return 'blr';
  return 'db';
}

export const GROUP_META: Record<
  ServerGroup,
  { label: string; description: string }
> = {
  db: {
    label: 'DB & Infrastructure',
    description: 'Application & database servers — no call counters.',
  },
  ahm: {
    label: 'AHM Dialers',
    description: 'Ahmedabad dialers — active call counters live here.',
  },
  blr: {
    label: 'BLR Dialers',
    description: 'Bangalore dialers — active call counters live here.',
  },
};

/** Color for a metric bar by its percent value. */
export function barColor(pct: number): string {
  if (pct >= 80) return '#e74a3b'; // red
  if (pct >= 50) return '#f6a623'; // amber
  return '#2ecc71'; // green
}

/** Status dot color keyed by severity. */
export function dotColorBySeverity(severity: MetricSeverity): string {
  if (severity === 'critical') return '#e74a3b';
  if (severity === 'warning') return '#f6a623';
  if (severity === 'offline') return '#94a3b8';
  return '#2ecc71';
}

/** Natural/numeric sort so VODAFONE7 < VODAFONE10. */
export function compareServerName(
  a: string | undefined,
  b: string | undefined,
): number {
  return (a ?? '').localeCompare(b ?? '', undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/** Format a JS Date as the server-side wire format. */
export function toServerDateTime(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export { isOnlineStatus };
